import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { ServicePartnerCreateRequest, ServicePartnerRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddServicePartner = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: ServicePartnerCreateRequest) => api.postServicepartnersV1(data),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("model.service.action.create.notification.success"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.service.action.create.notification.error"),
      });
    },
  });
};

const useUpdateServicePartner = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { id: string; data: ServicePartnerRequest }) =>
      api.putServicepartnersV1(payload.id, payload.data),
    onSuccess: async (_, payload) => {
      showFlashToast({
        variant: "success",
        message: t("model.service.action.edit.notification.success"),
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, payload.id) });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message: t("model.service.action.edit.notification.error"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, payload.id) });
    },
  });
};

const useDeleteServicePartner = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (id: string) => api.deleteServicepartnersByIdV1(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, id) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
      showFlashToast({
        variant: "success",
        message: t("model.service.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.service.action.delete.notification.error"),
      });
    },
  });
};

const useGiveIqbiConsent = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (_: string) => api.postServicepartnersIqbiConsentV1(),
    onSuccess: (_, serviceId) => {
      showFlashToast({
        variant: "success",
        message: t("model.service.action.iqbi-give-consent.notification.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, serviceId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.service.action.iqbi-give-consent.notification.error"),
      });
    },
  });
};

const useVerifyIqbiAddress = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (_: string) => api.postServicepartnersIqbiConfirmAddressV1(),
    onSuccess: (_, serviceId) => {
      showFlashToast({
        variant: "success",
        message: t("model.service.action.iqbi-verify.notification.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, serviceId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.service.action.iqbi-verify.notification.error"),
      });
    },
  });
};

const useRegisterMyPup = () => {
  const api = useApi();

  return useMutation({
    mutationFn: () => api.postServicepartnersMypupRegisterV1(),
  });
};

const useGetAccessLink = () => {
  const api = useApi();

  return useMutation({
    mutationFn: ({ servicePartnerId, returnUrl }: { servicePartnerId: string; returnUrl?: string }) =>
      api.getServicepartnersAccessLinkV1(servicePartnerId, returnUrl ? { returnUrl } : undefined).then((x) => x.data),
  });
};

export const servicePartnerMutations = {
  useAddServicePartner,
  useUpdateServicePartner,
  useDeleteServicePartner,
  useGiveIqbiConsent,
  useVerifyIqbiAddress,
  useRegisterMyPup,
  useGetAccessLink,
};
